'use client';

import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import NextLink from 'next/link';
import NextImage from 'next/image';
import { CalendarBlank, Clock, CaretRight, Article, Tag } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { blogApi, type BlogPostPreview } from '@/lib/api/blog';

// Детерминированное форматирование даты — НЕ используем toLocaleDateString
// потому что на серверной Node может не быть полной ICU-локали ('ru-RU'),
// а на клиенте есть → разный результат → hydration mismatch.
const MONTHS_RU_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS_RU_GENITIVE[d.getMonth()]}`;
}

function MiniCard({ post, index }: { post: BlogPostPreview; index: number }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      className="group"
    >
      <NextLink href={`/blog/${post.slug}`} className="block">
        <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-[var(--surface-secondary)] border border-[var(--border-default)] group-hover:border-[var(--primary)]/30 transition-all duration-300">
          {post.image_url ? (
            <NextImage
              src={post.image_url}
              alt={post.title}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              sizes="(max-width: 768px) 100vw, 33vw"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Article className="w-10 h-10 text-[var(--text-tertiary)]" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>

        <div className="mt-3">
          {post.tags.length > 0 && (
            <div className="flex gap-1.5 mb-2">
              {post.tags.slice(0, 2).map(tag => (
                <span key={tag} className="text-[10px] font-semibold uppercase tracking-wider text-[var(--primary)]">
                  {tag}
                </span>
              ))}
            </div>
          )}
          <h3 className="font-heading font-bold text-[var(--text-primary)] group-hover:text-[var(--primary)] transition-colors line-clamp-2 text-base leading-snug">
            {post.title}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-xs text-[var(--text-tertiary)]">
            <span className="flex items-center gap-1">
              <CalendarBlank className="w-3 h-3" /> {formatDate(post.published_at)}
            </span>
            {post.reading_time > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {post.reading_time} мин
              </span>
            )}
          </div>
        </div>
      </NextLink>
    </motion.article>
  );
}

export function BlogSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['blog-home'],
    queryFn: () => blogApi.getList(1, 3),
    staleTime: 120000,
  });

  const posts = data?.data || [];

  // Don't render if no posts
  if (!isLoading && posts.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-16 bg-[var(--bg-primary)]">
      {/* Header */}
      <div className="flex items-end justify-between mb-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-[var(--text-primary)]">
            Блог
          </h2>
          <p className="text-sm md:text-base text-[var(--text-secondary)] mt-1">
            Полезные статьи о японской кухне
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <NextLink
            href="/blog"
            className={cn(
              'inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full',
              'text-sm font-medium transition-all duration-300',
              'bg-[var(--primary)]/10 text-[var(--primary)]',
              'hover:bg-[var(--primary)] hover:text-white hover:shadow-lg hover:shadow-[var(--primary)]/25',
            )}
          >
            Все статьи <CaretRight className="w-4 h-4" weight="bold" />
          </NextLink>
        </motion.div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[16/10] rounded-2xl bg-[var(--surface-secondary)]" />
              <div className="mt-3 space-y-2">
                <div className="h-4 bg-[var(--surface-secondary)] rounded w-3/4" />
                <div className="h-3 bg-[var(--surface-secondary)] rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <MiniCard key={post.id} post={post} index={i} />
          ))}
        </div>
      )}
    </section>
  );
}
